import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import Logger from "../../../services/Logger";
import UtilService from "../../../services/UtilService";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomSnackbar from "../../components/CustomSnackbar";
import AdminJigsawService from "../../jigsaw/AdminJigsawService";

const DELETE_ALL_TARGET_ID = "__all__";

function boardLabel(play) {
    const tile = play?.tileSize ? `${play.tileSize}×${play.tileSize}` : null;
    const difficulty = play?.difficultyLevel || null;
    return [tile, difficulty].filter(Boolean).join(" · ") || "—";
}

function basketLabel(play) {
    const count = play?.basketCount ?? "?";
    const index = play?.basketIndex ?? "?";
    return `${Number(index) + 1} / ${count}`;
}

/**
 * Color Basket sessions ({@code jigsaw_color_basket_play}) for one player, with per-row and
 * delete-all cleanup. Removing a row only resets that basket board — the shared basket clustering
 * and any coin unlocks stay untouched.
 */
export default function JigsawColorBasketAdmin({ cloudUserId }) {
    const [plays, setPlays] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [deleting, setDeleting] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "success", message: "" });

    const subjectUserId = typeof cloudUserId === "string" ? cloudUserId.trim() : "";

    const showSnack = (severity, message) => setSnackbar({ open: true, severity, message });

    const loadPlays = useCallback(async () => {
        if (!subjectUserId) {
            setPlays([]);
            return;
        }
        setLoading(true);
        setError("");
        const result = await AdminJigsawService.listUserColorBasketPlays(subjectUserId);
        setLoading(false);
        if (!result.success) {
            setPlays([]);
            setError(result.error || "Failed to load color basket plays");
            return;
        }
        setPlays(Array.isArray(result.data?.plays) ? result.data.plays : []);
    }, [subjectUserId]);

    useEffect(() => {
        loadPlays();
    }, [loadPlays]);

    const openDeleteRow = (play) => {
        if (!play?.id) return;
        setDeleteTarget(play);
        setConfirmOpen(true);
    };

    const openDeleteAll = () => {
        setDeleteTarget(null);
        setConfirmOpen(true);
    };

    const handleConfirm = useCallback(async (targetId) => {
        if (!subjectUserId) return;
        setDeleting(true);
        const deleteAll = targetId === DELETE_ALL_TARGET_ID;
        const result = deleteAll
            ? await AdminJigsawService.deleteUserColorBasketPlays(subjectUserId)
            : await AdminJigsawService.deleteUserColorBasketPlay(subjectUserId, targetId);
        setDeleting(false);
        if (!result.success) {
            Logger.error("JigsawColorBasketAdmin delete:", result.error);
            showSnack("error", result.error || "Failed to delete color basket play");
            return;
        }
        const removed = Number(result.data?.deleted);
        showSnack(
            "success",
            deleteAll
                ? `Deleted ${Number.isFinite(removed) ? removed : 0} color basket record(s)`
                : "Color basket record deleted"
        );
        setDeleteTarget(null);
        loadPlays();
    }, [subjectUserId, loadPlays]);

    return (
        <Card variant="outlined">
            <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        Color Basket records
                    </Typography>
                    <Button
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={loadPlays}
                        disabled={loading || deleting || !subjectUserId}
                    >
                        Refresh
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteSweepOutlinedIcon />}
                        onClick={openDeleteAll}
                        disabled={loading || deleting || plays.length === 0}
                    >
                        Delete all ({plays.length})
                    </Button>
                </Box>

                {!subjectUserId ? (
                    <Alert severity="warning">User id is missing.</Alert>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : plays.length === 0 ? (
                    <Alert severity="info">No color basket records for this user.</Alert>
                ) : (
                    <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Story</TableCell>
                                    <TableCell>Board</TableCell>
                                    <TableCell>Basket</TableCell>
                                    <TableCell>State</TableCell>
                                    <TableCell align="right">Pieces</TableCell>
                                    <TableCell align="right">Transferred</TableCell>
                                    <TableCell>Started</TableCell>
                                    <TableCell>Updated</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {plays.map((play) => (
                                    <TableRow key={play.id} hover>
                                        <TableCell sx={{ maxWidth: 260 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {play.storyTitle || "(untitled)"}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                                            >
                                                {play.jigsawId || "—"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{boardLabel(play)}</TableCell>
                                        <TableCell>{basketLabel(play)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={play.solved ? "Solved" : play.playState || "—"}
                                                size="small"
                                                color={play.solved ? "success" : "default"}
                                                variant={play.solved ? "filled" : "outlined"}
                                            />
                                        </TableCell>
                                        <TableCell align="right">{play.piecesOnBoard ?? 0}</TableCell>
                                        <TableCell align="right">{play.transferredPieces ?? 0}</TableCell>
                                        <TableCell>{UtilService.formatDate(play.startedAt) || "—"}</TableCell>
                                        <TableCell>
                                            {UtilService.formatDate(play.updatedAt || play.createdAt) || "—"}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Delete this color basket record">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        disabled={deleting}
                                                        onClick={() => openDeleteRow(play)}
                                                        aria-label="Delete color basket record"
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(open) => setSnackbar((s) => ({ ...s, open }))}
                message={snackbar.message}
                severity={snackbar.severity}
            />

            <ConfirmDialog
                open={confirmOpen}
                setOpen={setConfirmOpen}
                title={deleteTarget ? "Delete color basket record?" : "Delete all color basket records?"}
                message={
                    deleteTarget
                        ? [
                            "This removes the row from jigsaw_color_basket_play, so the player starts this basket from scratch.",
                            "Pieces already transferred to the main board stay where they are.",
                        ]
                        : [
                            `This removes all ${plays.length} row(s) from jigsaw_color_basket_play for this player.`,
                            "Basket clustering and coin unlocks are not affected.",
                        ]
                }
                targetName={
                    deleteTarget
                        ? `${deleteTarget.storyTitle || "(untitled)"} · ${boardLabel(deleteTarget)} · basket ${basketLabel(deleteTarget)}`
                        : ""
                }
                targetId={deleteTarget?.id ?? DELETE_ALL_TARGET_ID}
                callback={handleConfirm}
            />
        </Card>
    );
}
